import { useMemo, useState } from 'react';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { notifications } from '@/api/endpoints';
import { errorMessage, pickList } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Loading,
  Select,
  StatusBadge,
} from '@/components/ui';
import { formatDateTime, str } from '@/utils/format';

export function NotificationsScreen({ mode = 'customer' }: { mode?: 'admin' | 'customer' }) {
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const state = useAsync(
    () => notifications.list(),
    (p) => pickList(p, 'notifications', 'items', 'data'),
  );

  const list = state.data ?? [];

  const filtered = useMemo(() => {
    if (!showUnreadOnly) return list;
    return list.filter((n) => !n.read_at && !n.is_read);
  }, [list, showUnreadOnly]);

  const unreadCount = useMemo(
    () => list.filter((n) => !n.read_at && !n.is_read).length,
    [list],
  );

  async function markRead(id: unknown) {
    const res = await notifications.read({ id, notification_id: id });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode={mode}
      icon="bell"
      title="الإشعارات"
      subtitle="تابع التنبيهات المهمة وعلّمها كمقروءة بعد المراجعة."
    >
      <MetricGrid>
        <MetricCard title="كل الإشعارات" value={String(list.length)} icon={<Icon name="bell" size={21} />} />
        <MetricCard title="غير مقروءة" value={String(unreadCount)} icon={<Icon name="deposit" size={21} />} />
      </MetricGrid>

      <FilterCard title="عرض الإشعارات" icon={<Icon name="settings" size={22} />}>
        <Select
          label="التصفية"
          value={showUnreadOnly ? 'unread' : 'all'}
          onChange={(v) => setShowUnreadOnly(v === 'unread')}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'unread', label: 'غير المقروءة فقط' },
          ]}
        />
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={16} /> تحديث
        </Button>
      </FilterCard>

      <SectionCard title={`الإشعارات (${filtered.length})`} icon={<Icon name="bell" size={24} />}>
        {filtered.length === 0 ? (
          <Empty title="لا توجد إشعارات" hint="لا توجد إشعارات جديدة." />
        ) : (
          <div className="list-stack">
            {filtered.map((n, i) => {
              const unread = !n.read_at && !n.is_read;
              return (
                <article key={str(n.id, String(i))} className="list-card compact">
                  <div className="list-head">
                    <div>
                      <strong>{str(n.title ?? n.subject)}</strong>
                      <small className="muted block">{formatDateTime(n.created_at)}</small>
                    </div>
                    {unread ? <StatusBadge status="pending" /> : <StatusBadge status="approved" />}
                  </div>
                  <p>{str(n.body ?? n.message)}</p>
                  {unread ? (
                    <Button type="button" variant="secondary" onClick={() => void markRead(n.id)}>
                      تعليم كمقروء
                    </Button>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
